import { z } from "zod";

// Hero Section
export const heroSectionContentSchema = z.object({
  eyebrow: z.string().optional(),
  headline: z.string().min(1, "Headline is required"),
  subheadline: z.string().min(1, "Subheadline is required"),
  ctaText: z.string().min(1, "CTA text is required"),
  ctaHref: z.string().min(1, "CTA href is required"),
  secondaryCta: z
    .object({
      text: z.string(),
      href: z.string(),
    })
    .optional(),
  ctaNote: z.string().optional(),
  backgroundImage: z.string().optional(),
  backgroundVideo: z
    .object({
      src: z.string().min(1),
      poster: z.string().optional(),
      mobilePoster: z.string().optional(),
      type: z.enum(["mp4", "webm"]).optional(),
      loop: z.boolean().optional(),
      mute: z.boolean().optional(),
      autoplay: z.boolean().optional(),
    })
    .optional(),
  variant: z.enum(["centered", "imageRight"]).optional(),
  backgroundVariant: z.enum(["soft", "bold"]).optional(),
  tone: z.enum(["light", "dark"]).optional(),
});

// Features Section
export const featureItemSchema = z.object({
  title: z.string().min(1),
  description: z.string().min(1),
  icon: z.string().optional(),
});

export const featuresSectionContentSchema = z.object({
  title: z.string().optional(),
  subtitle: z.string().optional(),
  features: z.array(featureItemSchema).min(1, "At least one feature required"),
  columns: z.union([z.literal(2), z.literal(3), z.literal(4)]).optional(),
  variant: z.enum(["grid", "list"]).optional(),
});

// Services Section
export const serviceItemSchema = z.object({
  name: z.string().min(1),
  description: z.string().min(1),
  icon: z.string().optional(),
  href: z.string().optional(),
});

export const servicesSectionContentSchema = z.object({
  title: z.string().optional(),
  subtitle: z.string().optional(),
  services: z.array(serviceItemSchema).min(1, "At least one service required"),
  groups: z
    .array(
      z.object({
        label: z.string().min(1),
        services: z.array(serviceItemSchema).min(1),
      })
    )
    .optional(),
  variant: z.enum(["grid", "cards"]).optional(),
});

// CTA Bar Section
export const ctaBarSectionContentSchema = z.object({
  eyebrow: z.string().optional(),
  title: z.string().min(1, "Title is required"),
  subtitle: z.string().optional(),
  primaryCta: z.object({
    label: z.string().min(1, "Primary CTA label is required"),
    href: z.string().min(1, "Primary CTA href is required"),
  }),
  secondaryCta: z
    .object({
      label: z.string().min(1),
      href: z.string().min(1),
    })
    .optional(),
});

// About Section
export const statItemSchema = z.object({
  value: z.string().min(1),
  label: z.string().min(1),
});

export const aboutSectionContentSchema = z.object({
  title: z.string().min(1, "Title is required"),
  description: z.string().min(1, "Description is required"),
  image: z.string().optional(),
  video: z
    .object({
      provider: z.enum(["youtube", "vimeo", "custom"]),
      videoId: z.string().optional(),
      src: z.string().optional(),
      aspectRatio: z.enum(["16/9", "4/3", "1/1"]).optional(),
      autoplay: z.boolean().optional(),
      controls: z.boolean().optional(),
      title: z.string().optional(),
    })
    .optional(),
  stats: z.array(statItemSchema).optional(),
  variant: z.enum(["imageLeft", "imageRight", "centered"]).optional(),
});

// Testimonials Section
export const testimonialItemSchema = z.object({
  quote: z.string().min(1),
  author: z.string().min(1),
  role: z.string().optional(),
  avatar: z.string().optional(),
  rating: z.number().min(0).max(5).optional(),
});

export const testimonialsSectionContentSchema = z.object({
  title: z.string().optional(),
  subtitle: z.string().optional(),
  testimonials: z.array(testimonialItemSchema).min(1, "At least one testimonial required"),
  variant: z.enum(["grid", "carousel"]).optional(),
});

// Reviews Section (alias of testimonials for reviews/ratings)
export const reviewsSectionContentSchema = testimonialsSectionContentSchema;

// Newsletter Section
export const newsletterSectionContentSchema = z.object({
  title: z.string().min(1),
  subtitle: z.string().optional(),
  placeholder: z.string().optional(),
  ctaText: z.string().optional(),
  successMessage: z.string().optional(),
  emailTo: z.string().optional(),
  provider: z.enum(["mailchimp", "convertkit", "custom"]).optional(),
  apiEndpoint: z.string().optional(),
});

// Blog Section
export const blogPostSchema = z.object({
  id: z.string().min(1),
  title: z.string().min(1),
  excerpt: z.string().min(1),
  author: z.string().optional(),
  date: z.string().min(1),
  image: z.string().optional(),
  category: z.string().optional(),
  href: z.string().min(1),
  readTime: z.string().optional(),
});

export const blogSectionContentSchema = z.object({
  title: z.string().optional(),
  subtitle: z.string().optional(),
  posts: z.array(blogPostSchema).min(1, "At least one post required"),
  variant: z.enum(["grid", "list", "masonry"]).optional(),
  columns: z.union([z.literal(2), z.literal(3)]).optional(),
});

// Logo Cloud Section
export const logoCloudSectionContentSchema = z.object({
  title: z.string().optional(),
  subtitle: z.string().optional(),
  logos: z.array(
    z.object({
      src: z.string().min(1).optional(),
      alt: z.string().optional(),
      href: z.string().optional(),
      width: z.number().optional(),
      height: z.number().optional(),
    })
  ).min(1, "At least one logo required"),
  variant: z.enum(["grid", "carousel"]).optional(),
  columns: z.union([z.literal(3), z.literal(4), z.literal(5), z.literal(6)]).optional(),
  grayscale: z.boolean().optional(),
});

// Pricing Section
export const pricingPlanSchema = z.object({
  name: z.string().min(1),
  price: z.string().min(1),
  description: z.string().optional(),
  features: z.array(z.string()),
  ctaText: z.string().min(1),
  ctaHref: z.string().min(1),
  highlighted: z.boolean().optional(),
});

export const pricingSectionContentSchema = z.object({
  title: z.string().optional(),
  subtitle: z.string().optional(),
  plans: z.array(pricingPlanSchema).min(1, "At least one plan required"),
  planGroups: z
    .array(
      z.object({
        id: z.string().min(1),
        label: z.string().min(1),
        plans: z.array(pricingPlanSchema).min(1),
      })
    )
    .optional(),
  variant: z.enum(["cards", "table"]).optional(),
});

// FAQ Section
export const faqItemSchema = z.object({
  question: z.string().min(1),
  answer: z.string().min(1),
  answerLink: z
    .object({
      label: z.string().min(1),
      href: z.string().min(1),
    })
    .optional(),
});

export const faqSectionContentSchema = z.object({
  title: z.string().optional(),
  subtitle: z.string().optional(),
  items: z.array(faqItemSchema).min(1, "At least one FAQ item required"),
  variant: z.enum(["accordion", "list"]).optional(),
});

// Contact Section
export const contactInfoItemSchema = z.object({
  type: z.enum(["email", "phone", "address"]),
  value: z.string().min(1),
  href: z.string().optional(),
});

export const contactSectionContentSchema = z.object({
  title: z.string().min(1, "Title is required"),
  subtitle: z.string().optional(),
  contactInfo: z.array(contactInfoItemSchema).optional(),
  showForm: z.boolean().optional(),
  variant: z.enum(["split", "centered"]).optional(),
});

// Timeline Section (shares services structure)
export const timelineSectionContentSchema = servicesSectionContentSchema;

// Events Section
export const eventItemSchema = z.object({
  title: z.string().min(1, "Event title is required"),
  description: z.string().optional(),
  date: z.string().min(1, "Event date is required"),
  time: z.string().optional(),
  location: z.string().optional(),
  href: z.string().optional(),
  linkText: z.string().optional(),
  category: z.string().optional(),
  blocked: z.boolean().optional(),
});

export const eventsSectionContentSchema = z.object({
  title: z.string().optional(),
  subtitle: z.string().optional(),
  events: z.array(eventItemSchema),
  calendarLink: z
    .object({
      text: z.string(),
      href: z.string(),
    })
    .optional(),
  variant: z.enum(["list", "grid", "timeline", "calendar"]).optional(),
});


// Team Section
export const teamMemberSchema = z.object({
  name: z.string().min(1),
  role: z.string().optional(),
  photo: z.string().optional(),
  bio: z.string().optional(),
});

export const teamSectionContentSchema = z.object({
  title: z.string().optional(),
  subtitle: z.string().optional(),
  members: z.array(teamMemberSchema).min(1, "At least one team member required"),
});
